import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faChevronRight,
  faHome,
  faSignInAlt,
} from "@fortawesome/free-solid-svg-icons";
import "./css/Events.css";

/**
 * Chrome shared by every public event page.
 *
 * These pages are reached by people with no session — often straight from a
 * flyer or a forwarded link — so they deliberately do not render the app
 * header, sidebar, or any auth-aware component. The bar and footer instead
 * mirror the marketing site's navy/logo treatment so the pages still read as
 * part of shield.kinprotect.com.
 *
 * `crumbs` takes { label, to? } entries; the last one renders as the current
 * page.
 */
const EventShell = ({ children, narrow = false, crumbs = [] }) => (
  <div className="ev-page">
    <header className="ev-header">
      <div className="ev-header-inner">
        <Link to="/events" className="ev-brand">
          <img
            className="ev-brand-logo"
            src="/KinShield.png"
            alt=""
            draggable={false}
          />
          <span className="ev-brand-name">KinShield™</span>
          <span className="ev-brand-tag">Events</span>
        </Link>

        {/* A <nav> here would inherit the app-wide `nav a` rule (white text). */}
        <div className="ev-header-nav">
          <Link className="ev-header-link" to="/events">
            <FontAwesomeIcon icon={faCalendarAlt} aria-hidden="true" />
            All events
          </Link>
          <Link className="ev-header-link" to="/">
            <FontAwesomeIcon icon={faHome} aria-hidden="true" />
            KinShield home
          </Link>
          <a className="ev-header-cta" href="/app">
            <FontAwesomeIcon icon={faSignInAlt} aria-hidden="true" />
            Sign in
          </a>
        </div>
      </div>
    </header>

    {crumbs.length > 0 && (
      <div className="ev-crumbs" role="navigation" aria-label="Breadcrumb">
        <ol className={`ev-crumbs-inner${narrow ? " ev-narrow" : ""}`}>
          {crumbs.map((crumb, index) => {
            const last = index === crumbs.length - 1;
            return (
              <li key={`${crumb.label}-${index}`}>
                {last || !crumb.to ? (
                  <span aria-current={last ? "page" : undefined}>
                    {crumb.label}
                  </span>
                ) : (
                  <Link to={crumb.to}>{crumb.label}</Link>
                )}
                {!last && (
                  <FontAwesomeIcon
                    className="ev-crumb-sep"
                    icon={faChevronRight}
                    aria-hidden="true"
                  />
                )}
              </li>
            );
          })}
        </ol>
      </div>
    )}

    <main className={`ev-container${narrow ? " ev-narrow" : ""}`}>
      {children}
    </main>

    <footer className="ev-footer">
      <div className="ev-footer-inner">
        <div className="ev-footer-col ev-footer-about">
          <h2 className="ev-footer-head">KinShield™ Events</h2>
          <p>
            Free, plain-language sessions on spotting scams, protecting your
            money, and staying safe online — for older adults, families,
            community groups, and small businesses.
          </p>
        </div>

        <div className="ev-footer-col">
          <h2 className="ev-footer-head">Events</h2>
          <ul>
            <li>
              <Link to="/events">All sessions</Link>
            </li>
            <li>
              <Link to="/events/host-request">Request a speaker</Link>
            </li>
          </ul>
        </div>

        <div className="ev-footer-col">
          <h2 className="ev-footer-head">KinShield</h2>
          <ul>
            <li>
              <Link to="/">Main site</Link>
            </li>
            <li>
              <a href="/app">Sign in</a>
            </li>
            <li>
              <Link to="/terms-and-conditions">Terms &amp; conditions</Link>
            </li>
            <li>
              <Link to="/privacy-policy">Privacy policy</Link>
            </li>
          </ul>
        </div>

        <div className="ev-footer-col">
          <h2 className="ev-footer-head">Contact</h2>
          <p>
            Reliable IT Associates, LLC
            <br />
            Maryland, USA
          </p>
          <p>
            <a href="mailto:info@kinprotect.com">info@kinprotect.com</a>
            <br />
            301-237-7843
          </p>
        </div>
      </div>

      <div className="ev-footer-bottom">
        &copy; {new Date().getFullYear()} Reliable IT Associates, LLC. All
        rights reserved.
      </div>
    </footer>
  </div>
);

export const EventLoading = ({ label = "Loading…" }) => (
  <div className="ev-spinner" role="status" aria-live="polite">
    <span className="ev-spinner-dot" aria-hidden="true" />
    {label}
  </div>
);

export const EventError = ({ message }) => (
  <div className="ev-alert ev-alert-error" role="alert">
    {message}
  </div>
);

export const EventSuccess = ({ message }) => (
  <div className="ev-alert ev-alert-success" role="status">
    {message}
  </div>
);

export default EventShell;
